import { BaseElement, defineElement } from "./lib-element-js.min.js";

/*
 * Bilder-Lazyload.
 *
 * Aenderungen gegenueber der Vorversion (Performance-Fix Startseite):
 * 1. Bilder im ersten Bildschirm laden weiterhin sofort. Das haelt den LCP niedrig.
 * 2. Vorlade-Abstand bleibt bei 1200px, analog zum nativen Lazy-Loading.
 * 3. Das Sicherheitsnetz laedt NICHT mehr pauschal alle Bilder nach 1,5 Sekunden.
 *    Genau das hat das Lazyload ausgehebelt: Auf der Startseite wurden dadurch
 *    rund 100 Bilder sofort geladen, obwohl nur eine Handvoll sichtbar ist.
 *    Stattdessen prueft ein gemeinsamer, per requestAnimationFrame gedrosselter
 *    Scroll-/Resize-Waechter, ob eine Kachel in Sichtnaehe geraten ist.
 * 4. Fallback, falls IntersectionObserver fehlt: sofort laden.
 * 5. Observer und Waechter werden nach dem Laden wieder abgemeldet.
 * 6. decoding="async" entlastet den Main-Thread beim Rendern.
 */

const PRELOAD_MARGIN_PX = 1200;
const PRELOAD_MARGIN = PRELOAD_MARGIN_PX + "px";
const EAGER_VIEWPORT_FACTOR = 1.5;

const pending = new Set();
let watcherActive = false;
let scheduled = false;

function viewportHeight() {
  return window.innerHeight || document.documentElement.clientHeight || 800;
}

function viewportWidth() {
  return window.innerWidth || document.documentElement.clientWidth || 1200;
}

function runWatch() {
  scheduled = false;
  Array.from(pending).forEach(function (el) {
    if (el.isNearViewport()) el.setSrcSet();
  });
  if (pending.size === 0) stopWatcher();
}

function schedule() {
  if (scheduled) return;
  scheduled = true;
  window.requestAnimationFrame(runWatch);
}

function startWatcher() {
  if (watcherActive) return;
  watcherActive = true;
  window.addEventListener("scroll", schedule, { passive: true, capture: true });
  window.addEventListener("resize", schedule, { passive: true });
}

function stopWatcher() {
  if (!watcherActive) return;
  watcherActive = false;
  window.removeEventListener("scroll", schedule, { capture: true });
  window.removeEventListener("resize", schedule);
}

class ImgLazyLoad extends BaseElement {
  image = this.querySelector("img");

  // lifecycle hook
  connected() {
    if (!this.image) return;

    if (this.isNearInitialViewport()) {
      this.setSrcSet();
      return;
    }

    this.observe();
    pending.add(this);
    startWatcher();
  }

  // reactive attributes/properties
  properties() {
    return {
      loaded: false,
    };
  }

  isNearInitialViewport() {
    const rect = this.getBoundingClientRect();
    return rect.top < viewportHeight() * EAGER_VIEWPORT_FACTOR;
  }

  // Sicherheitsnetz: nur laden, wenn die Kachel tatsaechlich in Sichtnaehe ist.
  isNearViewport() {
    const rect = this.getBoundingClientRect();
    const vh = viewportHeight();
    const vw = viewportWidth();
    const m = PRELOAD_MARGIN_PX;
    const verticallyNear = rect.top < vh + m && rect.bottom > -m;
    const horizontallyNear = rect.left < vw + m && rect.right > -m;
    return verticallyNear && horizontallyNear;
  }

  observe() {
    if (typeof IntersectionObserver === "undefined") {
      this.setSrcSet();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setSrcSet();
          }
        });
      },
      {
        rootMargin: PRELOAD_MARGIN,
      }
    );
    this.observer.observe(this);
  }

  // Nutzt das vorhandene responsive data-srcset statt das Originalbild in voller Aufloesung.
  setSrcSet() {
    if (this.loaded || !this.image) return;

    const srcset = this.image.getAttribute("data-srcset");
    if (!srcset) return;

    const width = this.image.offsetWidth || this.offsetWidth || 533;
    this.image.setAttribute("sizes", width + "px");
    this.image.setAttribute("decoding", "async");
    this.image.setAttribute("loading", "lazy");
    this.image.setAttribute("srcset", srcset);
    this.loaded = true;

    this.cleanup();
  }

  cleanup() {
    pending.delete(this);
    if (pending.size === 0) stopWatcher();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

defineElement("img-lazy-load", ImgLazyLoad);
